export interface InvoiceType {
    id: number | null;
    uuid?: string;
    created: string | null;
    modified: string | null;
    number: string | null;
    internal_reference: string | null;
    external_reference: string | null;
    labels: number[];
    deal: number | null;
    proposal: number | null;
    order: number | null;
    job: number | null;
    project: { id: number, name: string, number: string | null } | null;
    recipient: number | null;
    recipient_person: number | null;
    invoice_address: number | null;
    invoice_date: string | null;
    due_date: string | null;
    expiration_days: number | null;
    terms_and_conditions: string | null;
    condition_lines: { label: string, value: string }[];
    tax_blocked_account_text: string | null;
    amount: string;
    amount_paid: string;
    amount_due: string;
    vat_method: string;
    price_display_mode: string;
    currency: string;
    is_paid: boolean;
    workflow: number;
    status: string;
    deleted: boolean;
    division: number | null;
    import_tag: number | null;
};

export interface DocumentLineType {
    id: number | null;
    uuid?: string;
    invoice: number | null;
    proposal: number | null;
    order: number | null;
    product: number | null;
    service: number | null;
    hourly_rate: number | null;
    quantity_string: string | null;
    quantity: number | null;
    unit: string | null;
    description: string;
    sale_price_string: string | null;
    sale_price: number | null;
    sale_price_currency: string | null;
    total_price: number | null;
    vat_rate: number | null;
    ledger_account: number | null;
    discount: number | null;
    // Size in whole centimeters of lines with a width x depth priced product
    width?: number | null;
    depth?: number | null;
    // The pricing method of the product of the line, given by the backend
    product_pricing_method?: 'per_unit' | 'dimensions' | null;
    // The saved image url and the upload fields of a new or removed image
    image?: string | null;
    image_file?: string | null;
    image_filename?: string | null;
    image_deleted?: boolean;
    // A real group id once saved, or the uuid of a not yet saved group
    group: number | string | null;
    position: number | null;
    deleted: boolean;
};

export const createNewDocumentLine = (
    documentId: number, documentType: 'proposal' | 'order' | 'invoice', uuid: string, currency: string, position: number, group: number | string | null = null
): DocumentLineType => ({
    id: null,
    uuid: uuid,
    invoice: documentType === 'invoice' ? documentId : null,
    proposal: documentType === 'proposal' ? documentId : null,
    order: documentType === 'order' ? documentId : null,
    product: null,
    service: null,
    hourly_rate: null,
    quantity_string: '1 st',
    quantity: 1,
    unit: 'st',
    description: '',
    sale_price_string: null,
    sale_price: null,
    sale_price_currency: currency,
    total_price: null,
    vat_rate: null,
    ledger_account: null,
    discount: null,
    width: null,
    depth: null,
    product_pricing_method: null,
    image: null,
    group: group,
    position: position,
    deleted: false,
});

export interface DocumentLineGroupType {
    id: number | null;
    uuid?: string;
    title: string;
    description: string | null;
    position: number | null;
    deleted: boolean;
};

export const createNewDocumentLineGroup = (uuid: string, position: number): DocumentLineGroupType => ({
    id: null,
    uuid: uuid,
    title: '',
    description: null,
    position: position,
    deleted: false,
});

export interface Payment {
    id: number;
    invoice: number;
    date: string;
    amount: string;
    currency: string;
    payment_method: string;
    note: string | null;
    registrated_by: number | null;
    timeline_item: number;
    deleted: boolean;
}

// Type of the schedule of a periodic invoice
export interface InvoiceScheduleType {
    id: number;
    modified: string;
    template: number;
    contract: number | null;
    contract_number?: string | null;
    contract_reference?: string | null;
    interval_type: 'weekly' | 'monthly' | 'quarterly' | 'half_yearly' | 'yearly';
    first_invoice_date: string;
    end_date: string | null;
    last_generated_date: string | null;
    next_invoice_date: string | null;
    monthly_revenue: string;
    unsent_invoice_count: number;
    sent_invoice_count: number;
    is_active: boolean;
    deleted: boolean;
}

// Type of an invoice template, the base invoice of a periodic invoice
export interface InvoiceTemplateType {
    id: number;
    number: string | null;
    internal_reference: string | null;
    external_reference: string | null;
    recipient: Record<string, any> | null;
    recipient_name?: string | null;
    amount: string;
    currency: string;
    workflow: number | Record<string, any> | null;
    deleted: boolean;
    schedule: InvoiceScheduleType | null;
    schedule_is_active?: boolean;
}
